import * as v from 'valibot';

const layoutEntrySchema = v.object({
  x: v.pipe(v.number(), v.minValue(0)),
  y: v.pipe(v.number(), v.minValue(0)),
  w: v.pipe(v.number(), v.minValue(1)),
  h: v.pipe(v.number(), v.minValue(1)),
  minW: v.optional(v.number()),
  minH: v.optional(v.number()),
  maxW: v.optional(v.number()),
  maxH: v.optional(v.number()),
});

const layoutSchema = v.object({
  lg: v.optional(layoutEntrySchema),
  md: v.optional(layoutEntrySchema),
  sm: v.optional(layoutEntrySchema),
  xs: v.optional(layoutEntrySchema),
  xxs: v.optional(layoutEntrySchema),
});

export const baseWidgetSchema = v.object({
  id: v.string(),
  layout: layoutSchema,
  config: v.optional(v.record(v.string(), v.unknown())),
});

export type BaseWidget = v.InferOutput<typeof baseWidgetSchema>;

const dashboardWidgetIds = [
  'awol',
  'behaviour',
  'timetable-widget',
  'school-calendar-widget',
  'overdue-attendance-widget',
  'priority-student-widget',
  'highlight-widget',
  'assessments-widget',
  'staff-drive-widget',
] as const;

export const dashboardWidgetSchema = v.object({
  id: v.picklist([...dashboardWidgetIds]),
  layout: layoutSchema,
});

export type DashboardWidget = v.InferOutput<typeof dashboardWidgetSchema>;

const chartConfigSchema = v.object({
  type: v.literal('chart'),
  chartType: v.string(),
  title: v.optional(v.string()),
  filter: v.optional(v.record(v.string(), v.unknown())),
  options: v.optional(v.record(v.string(), v.unknown())),
});

const tableConfigSchema = v.object({
  type: v.literal('table'),
  filter: v.optional(v.record(v.string(), v.unknown())),
});

const figuresConfigSchema = v.object({
  type: v.literal('figures'),
  filter: v.optional(v.record(v.string(), v.unknown())),
  options: v.optional(
    v.object({
      rows: v.optional(v.number()),
      columns: v.optional(v.number()),
    }),
  ),
});

const reportWidgetConfigSchema = v.variant('type', [
  chartConfigSchema,
  tableConfigSchema,
  figuresConfigSchema,
]);

export const reportWidgetSchema = v.object({
  ...baseWidgetSchema.entries,
  config: reportWidgetConfigSchema,
});

export type ReportWidget = v.InferOutput<typeof reportWidgetSchema>;

// --- V2 Dashboard Widget Schema (type-discriminated) ---

const dashboardWidgetTypes = [
  'dashboard-awol',
  'dashboard-behaviour',
  'dashboard-timetable',
  'dashboard-school-calendar',
  'dashboard-overdue-attendance',
  'dashboard-priority-students',
  'dashboard-highlights',
  'dashboard-assessments',
  'dashboard-staff-drive',
] as const;

export type DashboardWidgetType = (typeof dashboardWidgetTypes)[number];

const reportWidgetTypes = [
  'report-chart',
  'report-table',
  'report-figures',
] as const;

export type ReportWidgetType = (typeof reportWidgetTypes)[number];

export type WidgetType = DashboardWidgetType | ReportWidgetType;

function dashboardTypeSchema<T extends string>(type: T) {
  return v.object({
    type: v.literal(type),
    id: v.string(),
    layout: layoutSchema,
    meta: v.optional(v.record(v.string(), v.unknown())),
  });
}

const dashboardAwolSchema = dashboardTypeSchema('dashboard-awol');
const dashboardBehaviourSchema = dashboardTypeSchema('dashboard-behaviour');
const dashboardTimetableSchema = dashboardTypeSchema('dashboard-timetable');
const dashboardSchoolCalendarSchema = dashboardTypeSchema(
  'dashboard-school-calendar',
);
const dashboardOverdueAttendanceSchema = dashboardTypeSchema(
  'dashboard-overdue-attendance',
);
const dashboardPriorityStudentsSchema = dashboardTypeSchema(
  'dashboard-priority-students',
);
const dashboardHighlightsSchema = dashboardTypeSchema('dashboard-highlights');
const dashboardAssessmentsSchema = dashboardTypeSchema('dashboard-assessments');
const dashboardStaffDriveSchema = dashboardTypeSchema('dashboard-staff-drive');

const groupingSchema = v.object({
  fieldId: v.string(),
  timeBucket: v.optional(v.string()),
});

const reportChartMetaSchema = v.object({
  reportId: v.string(),
  title: v.optional(v.string()),
  chartType: v.optional(v.string()),
  metricId: v.optional(v.string()),
  groupings: v.optional(v.array(groupingSchema)),
  filter: v.optional(v.record(v.string(), v.unknown())),
  options: v.optional(v.record(v.string(), v.unknown())),
});

const reportChartWidgetSchema = v.object({
  type: v.literal('report-chart'),
  id: v.string(),
  layout: layoutSchema,
  meta: reportChartMetaSchema,
});

const reportTableMetaSchema = v.object({
  reportId: v.string(),
  title: v.optional(v.string()),
  metricId: v.optional(v.string()),
  groupings: v.optional(v.array(groupingSchema)),
  filter: v.optional(v.record(v.string(), v.unknown())),
});

const reportTableWidgetSchema = v.object({
  type: v.literal('report-table'),
  id: v.string(),
  layout: layoutSchema,
  meta: reportTableMetaSchema,
});

const reportFiguresMetaSchema = v.object({
  reportId: v.string(),
  title: v.optional(v.string()),
  metricId: v.optional(v.string()),
  groupings: v.optional(v.array(groupingSchema)),
  filter: v.optional(v.record(v.string(), v.unknown())),
  // Legacy fields, retained for backwards compatibility with persisted data.
  metricIds: v.optional(v.array(v.string())),
  options: v.optional(
    v.object({
      rows: v.optional(v.number()),
      columns: v.optional(v.number()),
    }),
  ),
});

const reportFiguresWidgetSchema = v.object({
  type: v.literal('report-figures'),
  id: v.string(),
  layout: layoutSchema,
  meta: reportFiguresMetaSchema,
});

export const dashboardWidgetSchemaV2 = v.variant('type', [
  dashboardAwolSchema,
  dashboardBehaviourSchema,
  dashboardTimetableSchema,
  dashboardSchoolCalendarSchema,
  dashboardOverdueAttendanceSchema,
  dashboardPriorityStudentsSchema,
  dashboardHighlightsSchema,
  dashboardAssessmentsSchema,
  dashboardStaffDriveSchema,
  reportChartWidgetSchema,
  reportTableWidgetSchema,
  reportFiguresWidgetSchema,
]);

export type DashboardWidgetV2 = v.InferOutput<typeof dashboardWidgetSchemaV2>;

// Maps legacy widget IDs (no type field) to V2 type strings
const legacyIdToType: Record<string, DashboardWidgetType> = {
  awol: 'dashboard-awol',
  behaviour: 'dashboard-behaviour',
  'timetable-widget': 'dashboard-timetable',
  'school-calendar-widget': 'dashboard-school-calendar',
  'overdue-attendance-widget': 'dashboard-overdue-attendance',
  'priority-student-widget': 'dashboard-priority-students',
  'highlight-widget': 'dashboard-highlights',
  'assessments-widget': 'dashboard-assessments',
  'staff-drive-widget': 'dashboard-staff-drive',
};

export function normaliseDashboardWidget(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw;
  const obj = raw as Record<string, unknown>;
  if ('type' in obj) return obj;
  if ('id' in obj && typeof obj.id === 'string') {
    const type = legacyIdToType[obj.id];
    if (type) {
      return { ...obj, type };
    }
  }
  return obj;
}

export function validateDashboardWidgets(
  rawWidgets: unknown[],
): DashboardWidgetV2[] {
  return validateWidgets(
    rawWidgets.map(normaliseDashboardWidget),
    dashboardWidgetSchemaV2,
  );
}

export function validateWidgets<T>(
  widgets: unknown[],
  schema: v.GenericSchema<unknown, T>,
): T[] {
  const validWidgets: T[] = [];

  for (const widget of widgets) {
    const result = v.safeParse(schema, widget);
    if (result.success) {
      validWidgets.push(result.output);
    } else {
      import('@sentry/react')
        .then((Sentry) => {
          Sentry.captureMessage('Invalid widget filtered from layout config', {
            level: 'warning',
            extra: {
              widget,
              issues: result.issues,
            },
          });
        })
        .catch(() => {});
    }
  }

  return validWidgets;
}
